import {
  Component,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  NgZone,
  ChangeDetectorRef,
  OnDestroy,
  ViewChild,
  Renderer2,
  HostBinding
} from '@angular/core';
import { FieldWrapper } from '@ngx-formly/core';
import { FormViewService } from '@xilo-mono/form-contracts';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'formly-wrapper-hotkeys',
  templateUrl: './hotkeys-wrapper.component.html',
  styleUrls: ['./hotkeys-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormlyHotkeysWrapperComponent extends FieldWrapper implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('inputWrapper') inputWrapperEl?: ElementRef;
  @ViewChild('labelButtonsRow') labelButtonsRowEl?: ElementRef;
  @HostBinding('class.single-column-layout') get isSingleColumnLayout() {
    return this.detectSingleColumnLayout();
  }
  
  showHotkeys = false;
  isIntakeForm = false;
  compactMode = false;
  private readonly SAFE_MARGIN = 24;
  private readonly formViewService = inject(FormViewService);
  private readonly elRef = inject(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly renderer = inject(Renderer2);
  private readonly destroyed$ = new Subject<void>();
  private keydownListener?: () => void;
  private documentKeydownListener?: () => void;
  private addressNavigationListener?: () => void;
  private resizeObserver?: ResizeObserver;
  private compactCheckTimeout: any = null;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.isIntakeForm = this.formViewService?.formView?.components?.[0]?.type === 'intake-form';
  }

  ngAfterViewInit(): void {
    if (!this.isIntakeForm) return;
  
    this.zone.runOutsideAngular(() => {
      this.setupKeyListener();
      this.addressNavigationListener = this.renderer.listen(
        this.elRef.nativeElement,
        'addressNavigationKeyPressed',
        (event: CustomEvent) => {
          if (!this.field?.formControl?.valid) return;
          if (event.detail.modifiers.shift) {
            this.zone.run(() => this.focusNextSection());
          } else if (event.detail.modifiers.meta || event.detail.modifiers.ctrl) {
            this.zone.run(() => this.onSubmit());
          }
        }
      );
      this.setupResizeObserver();
      this.renderer.listen(this.elRef.nativeElement, 'xiloRadioInteraction', () => {
        this.cancelCompactCheck();
        this.compactCheckTimeout = setTimeout(() => {
          this.zone.run(() => {
            this.safeCheckCompactMode();
          });
        }, 300);
      });
      this.renderer.listen(this.elRef.nativeElement, 'change', () => {
        this.cancelCompactCheck();
        this.compactCheckTimeout = setTimeout(() => {
          this.zone.run(() => {
            this.safeCheckCompactMode();
          });
        }, 200);
      });
    });

    this.field.formControl?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.cancelCompactCheck();
        this.compactCheckTimeout = setTimeout(() => {
          this.zone.run(() => {
            this.safeCheckCompactMode();
          });
        }, 200);
      });
    setTimeout(() => {
      this.safeCheckCompactMode();
    }, 300);
  }

  ngOnDestroy(): void {
    this.keydownListener?.();
    this.documentKeydownListener?.();
    this.addressNavigationListener?.();
    this.resizeObserver?.disconnect();
    this.cancelCompactCheck();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.showHotkeys) {
          this.cancelCompactCheck();
          this.compactCheckTimeout = setTimeout(() => {
            this.zone.run(() => {
              this.safeCheckCompactMode();
            });
          }, 200);
        }
      });
      
      const inputWrapper = this.inputWrapperEl?.nativeElement ?? 
        this.elRef.nativeElement.querySelector('.input-wrapper');
      
      if (inputWrapper) {
        this.resizeObserver.observe(inputWrapper);
      }
    }
  }

  private cancelCompactCheck(): void {
    if (this.compactCheckTimeout) {
      clearTimeout(this.compactCheckTimeout);
      this.compactCheckTimeout = null;
    }
  }

  private setupKeyListener(): void {
    this.keydownListener = this.renderer.listen(this.elRef.nativeElement, 'keydown', (event: KeyboardEvent) => {
      this.handleKeydown(event);
    });

    this.documentKeydownListener = this.renderer.listen(document, 'keydown', (event: KeyboardEvent) => {
      if (this.elRef.nativeElement.contains(document.activeElement)) {
        const isAddressField = document.activeElement?.hasAttribute('ngx-google-places-autocomplete');
        if (!isAddressField) {
          this.handleKeydown(event);
        }
      }
    });
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (!this.field?.formControl?.valid) return;
    const isShiftEnter = event.key === 'Enter' && event.shiftKey;

    if (isShiftEnter) {
      event.preventDefault();
      event.stopPropagation();
      (document.activeElement as HTMLElement)?.blur();
      this.focusNextSection();
    }

    if (event.key === 'Tab' && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      this.focusNextField();
    }
  }

  get isMac(): boolean {
    return this.formViewService.isMacUser;
  }

  onSubmit(): void {
    this.formViewService.setFormAsSubmitted();
    this.formViewService.submitted = true;
    if (this.formViewService.formState.value.valid) {
      this.formViewService.onSubmitForm(true);
    }
  }

  isSectionValid(): boolean {
    return this.field?.formControl?.parent?.valid ?? false;
  }

  onFocus(): void {
    if (!this.isIntakeForm) return;
    this.showHotkeys = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.safeCheckCompactMode();
      this.cdr.markForCheck();
    }, 0);
  }

  onBlur(): void {
    setTimeout(() => {
      const isElementOrChildFocused = this.elRef.nativeElement.contains(document.activeElement);
      if (!isElementOrChildFocused) {
        this.showHotkeys = false;
        this.compactMode = false;
        this.cdr.markForCheck();
      }
    }, 100);
  }
  
  private detectSingleColumnLayout(): boolean {
    const checkSingleColumn = (obj: any): boolean | undefined => {
      if (!obj) return undefined;
      if (obj.templateOptions?.singleColumn !== undefined) {
        return obj.templateOptions.singleColumn;
      }
      return undefined;
    };
    const isSingleColumn = 
      checkSingleColumn(this.field) !== undefined ? checkSingleColumn(this.field) :
      checkSingleColumn(this.field?.parent) !== undefined ? checkSingleColumn(this.field?.parent) :
      checkSingleColumn(this.field?.parent?.parent) !== undefined ? checkSingleColumn(this.field?.parent?.parent) :
      false;
    
    const parentElement = this.elRef.nativeElement?.parentElement;
    const hasSingleColumnClass = parentElement?.classList?.contains('form-section-single-column');
    return isSingleColumn || !!hasSingleColumnClass;
  }
  
  private safeCheckCompactMode(): void {
    if (!this.showHotkeys) return;

    const measurementDiv = document.createElement('div');
    measurementDiv.style.cssText = 'position: absolute; visibility: hidden; pointer-events: none; left: -9999px;';
    document.body.appendChild(measurementDiv);
    
    try {
      const inputWrapper = this.inputWrapperEl?.nativeElement ?? 
        this.elRef.nativeElement.querySelector('.input-wrapper');
      const labelEl = this.elRef.nativeElement.querySelector('label');
      
      if (!inputWrapper || !labelEl) {
        document.body.removeChild(measurementDiv);
        return;
      }
      const labelClone = labelEl.cloneNode(true);
      measurementDiv.appendChild(labelClone);
      const labelWidth = labelClone.getBoundingClientRect().width;
      const labelButtonsRow = this.labelButtonsRowEl?.nativeElement ?? 
        this.elRef.nativeElement.querySelector('.label-buttons-row');
      
      if (!labelButtonsRow) {
        document.body.removeChild(measurementDiv);
        return;
      }
      
      const buttonContainer = document.createElement('div');
      buttonContainer.style.display = 'flex';
      measurementDiv.appendChild(buttonContainer);
      
      const buttons = labelButtonsRow.querySelectorAll('.hotkey-button');
      
      Array.from(buttons).forEach((btn: HTMLElement) => {
        const btnClone = btn.cloneNode(true) as HTMLElement;
        btnClone.classList.remove('compact');
        buttonContainer.appendChild(btnClone);
      });
      
      let buttonsWidth = buttonContainer.getBoundingClientRect().width;
      
      if (buttons.length > 1) {
        buttonsWidth += 10;
      }

      const availableWidth = inputWrapper.getBoundingClientRect().width;
      const totalNeededWidth = labelWidth + buttonsWidth + this.SAFE_MARGIN;
      const shouldCompact = totalNeededWidth > availableWidth;

      if (this.compactMode !== shouldCompact) {
        this.compactMode = shouldCompact;
        this.cdr.detectChanges();
      }
    } finally {
      document.body.removeChild(measurementDiv);
    }
  }

  focusNextField(): void {
    const selector = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';
    const focusables = Array.from(document.querySelectorAll<HTMLElement>(selector))
      .filter(el => el.offsetParent !== null &&
        !el.hasAttribute('disabled') &&
        getComputedStyle(el).display !== 'none' &&
        getComputedStyle(el).visibility !== 'hidden');

    const currentIndex = focusables.findIndex(el => el === document.activeElement);
    if (currentIndex !== -1 && currentIndex < focusables.length - 1) {
      focusables[currentIndex + 1]?.focus();
    }
  }

  focusNextSection(): void {
    const currentPanelEl = this.findCurrentAccordionPanel();
    if (!currentPanelEl) return;

    const allPanels = Array.from(document.querySelectorAll('xilo-accordion-panel'))
      .filter((el: any) => el?.offsetParent !== null);

    const currentIndex = allPanels.findIndex(p => p === currentPanelEl);
    if (currentIndex === -1 || currentIndex >= allPanels.length - 1) return;

    const nextPanelEl = allPanels[currentIndex + 1] as HTMLElement;
    if (!nextPanelEl) return;

    (document.activeElement as HTMLElement)?.blur();

    this.updateAccordionState(currentPanelEl.id || '', false);
    this.updateAccordionState(nextPanelEl.id || '', true);

    setTimeout(() => {
      const selector = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';
      const nextInput = nextPanelEl.querySelector<HTMLElement>(selector);
      if (nextInput?.offsetParent !== null) {
        nextPanelEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        nextInput.focus();
      }
    }, 200);
  }

  private findCurrentAccordionPanel(): HTMLElement | null {
    let current = this.elRef.nativeElement;
    while (current && current.tagName !== 'XILO-ACCORDION-PANEL') {
      current = current.parentElement;
    }
    return current ?? this.elRef.nativeElement.closest?.('xilo-accordion-panel') ?? null;
  }

  private updateAccordionState(panelId: string, isOpen: boolean): void {
    if (!panelId) return;
    this.formViewService.updateOpenedAccordions(panelId, isOpen);
  }
}